import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Show } from '../show';

import styled, { css } from 'styled-components';

import MinusIconPath from './assets/stroke-circle-minus.svg?react';
import PlusIconPath from './assets/stroke-circle-plus.svg?react';

import FluidContainer from '../layouts/components/fluid-container';
import ScoreCounter from '../score-counter';
import Stack from '../stack';
import { SOURCES } from '../../constants/sources';
import { CORRECT, INCOMPLETE, MISSING, WAITING_FOR_CORRECTION, WRONG } from '../../lib/exercise';

export const AccordionStatus = {
    correct: 'CORRECT',
    missing: 'MISSING',
    incomplete: 'INCOMPLETE',
    waiting: 'WAITING_FOR_CORRECTION',
    wrong: 'WRONG',
};

const accordionColors = {
    [AccordionStatus.correct]: 'var(--color-success-default)',
    [AccordionStatus.missing]: 'var(--gray200)',
    [AccordionStatus.incomplete]: 'var(--color-error-default)',
    [AccordionStatus.wrong]: 'var(--color-error-default)',
    [AccordionStatus.waiting]: 'var(--color-warning-default)',
};

export const Container = styled(FluidContainer)`
    justify-content: space-between;
    box-shadow: 0 2px 4px 0 rgba(66, 69, 72, 0.4);
    border-radius: 4px;
    ${(props) =>
        !props.solution &&
        css`
            border-left: 8px solid ${(props) => accordionColors[props.status]};
        `}

    ${(props) =>
        props.show ||
        css`
            display: none;
        `}

    @media print {
        display: block;
    }
    margin-bottom: 8px;
`;

export const CollapseButton = styled.div`
    width: 18px;
    height: 18px;
    cursor: pointer;
`;

export const MinusIcon = styled(MinusIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['surface-grey-800']};
    `}
`;

export const PlusIcon = styled(PlusIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['surface-grey-800']};
    `}
`;

export const Body = styled.div`
    width: 100%;
    display: none;
    ${(props) =>
        props.isOpened &&
        css`
            display: block;
        `};

    @media print {
        display: block;
    }
`;

const Ellipsis = styled.span`
    position: absolute;
    bottom: 0;
    right: 0;
    background: white;
    display: block;
`;

export const Title = styled.div`
    font-weight: 600;
    line-height: 24px;
    overflow: hidden;
    width: 100%;
    position: relative;
`;

export const Header = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    height: 40px;
    padding: 8px 16px;
    cursor: pointer;
`;

const StackContainer = styled.div`
    float: right;
    height: 24px;
    align-items: center;
    display: flex;
`;

const Text = styled.div`
    width: max-content;
`;

const FILTER_STATUS_MAP = {
    [MISSING]: MISSING,
    [WRONG]: WRONG,
    [CORRECT]: CORRECT,
    [INCOMPLETE]: WRONG,
    [WAITING_FOR_CORRECTION]: WAITING_FOR_CORRECTION,
};

const getTitle = (number, title, exerciseId, solution) => {
    if (solution && exerciseId) {
        return `${number}. ${title} | Id esercizio:#${exerciseId}`;
    }
    return `${number}. ${title}`;
};

const AccordionResult = ({
    children,
    expand,
    filter,
    number,
    title,
    status = 'MISSING',
    solution,
    exerciseId,
    difficulty,
    points,
    hasPartialPoints,
    source,
    setOverSizeClass,
}) => {
    const [isOpen, setIsOpen] = useState(expand);
    const [ellipsis, setEllipsis] = useState(false);

    const titleRef = useRef(null);
    const textRef = useRef(null);

    useEffect(() => {
        setIsOpen(expand);
    }, [expand]);

    useEffect(() => {
        if (titleRef.current.scrollHeight > 24) {
            setOverSizeClass();
        }

        if (textRef.current.offsetWidth > titleRef.current.clientWidth) {
            setEllipsis(true);
        } else {
            setEllipsis(false);
        }
    }, [setOverSizeClass]);

    return (
        <Container status={status} show={filter === 'ALL' || filter === FILTER_STATUS_MAP[status]} solution={solution} flexWrap="wrap">
            <Header onClick={() => setIsOpen(!isOpen)}>
                <Title ref={titleRef}>
                    <StackContainer>
                        <Stack direction="horizontal" gap="small" width="fit-content">
                            {source !== SOURCES.ZTE_UNITUTOR && source !== SOURCES.TT && hasPartialPoints && (
                                <ScoreCounter points={points} total={difficulty ? difficulty : 0} />
                            )}
                            <CollapseButton>{isOpen ? <MinusIcon /> : <PlusIcon />}</CollapseButton>
                        </Stack>
                    </StackContainer>
                    <Text>
                        <span ref={textRef}>{getTitle(number, title, exerciseId, solution)}</span>
                    </Text>
                    <Show when={ellipsis}>
                        <Ellipsis>{'...'}</Ellipsis>
                    </Show>
                </Title>
            </Header>
            <Body isOpened={isOpen}>{children}</Body>
        </Container>
    );
};

AccordionResult.propTypes = {
    children: PropTypes.node,
    exerciseId: PropTypes.number,
    expand: PropTypes.bool,
    solution: PropTypes.bool,
    filter: PropTypes.string,
    number: PropTypes.number,
    status: PropTypes.oneOf([
        AccordionStatus.correct,
        AccordionStatus.missing,
        AccordionStatus.waiting,
        AccordionStatus.wrong,
        AccordionStatus.incomplete,
    ]),
    title: PropTypes.string,
    difficulty: PropTypes.number,
    points: PropTypes.number,
    hasPartialPoints: PropTypes.bool,
    source: PropTypes.string,
    setOverSizeClass: PropTypes.func,
};

export default AccordionResult;
